import { Component, computed, signal, OnInit, OnDestroy, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NotificationService, Notification } from '../../../core/services/notifications/notification.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Spinner } from '../spinner/spinner';
import { Router } from '@angular/router';

@Component({
  selector: 'app-notifications',
  templateUrl: './notifications.html',
  styleUrls: ['./notifications.scss'],
  standalone: true,
  imports: [CommonModule, Spinner]
})
export class NotificationsComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  
  activeTab = signal<'whats-new' | 'previous'>('whats-new');
  
  // Track image loading errors
  imageLoadError: { [key: number]: boolean } = {};
  
  // Use service signals (initialized after constructor)
  allNotifications = computed(() => this.notificationService.notifications());
  unreadCount = computed(() => this.notificationService.unreadCount());
  isLoading = computed(() => this.notificationService.isLoading());
  isInitialized = computed(() => this.notificationService.isInitialized());
  
  // Filtered notifications based on active tab
  notifications = computed(() => {
    const allNotifs = this.allNotifications();
    const tab = this.activeTab();
    
    let filtered: Notification[];
    
    if (tab === 'whats-new') {
      // Show unread notifications (What's New)
      filtered = allNotifs.filter(n => !n.isRead);
    } else {
      // Show read notifications (Previous)
      filtered = allNotifs.filter(n => n.isRead);
    }
    
    // Sort: Pinned notifications first, then by created date (newest first)
    return filtered.sort((a, b) => {
      // First, sort by pinned status
      if (a.isPinned && !b.isPinned) return -1;
      if (!a.isPinned && b.isPinned) return 1;
      
      // If both have same pinned status, sort by creation date (newest first)
      const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return dateB - dateA;
    });
  });

  constructor(public notificationService: NotificationService, private router: Router) {
  }

  ngOnInit(): void {
    // Notify service that panel is open
    this.notificationService.setPanelOpen(true);

    // Listen for new notifications
    this.notificationService.newNotification$
      .pipe(takeUntil(this.destroy$))
      .subscribe(notification => {
        console.log('NotificationsComponent: New notification received:', notification);
      });
  }

  ngOnDestroy(): void {
    // Notify service that panel is closed
    this.notificationService.setPanelOpen(false);
    
    // Clean up
    this.destroy$.next();
    this.destroy$.complete();
  }

  setActiveTab(tab: 'whats-new' | 'previous') {
    this.activeTab.set(tab);
  }

  clearAll() {
    // Clear all notifications in the current tab
    const currentTab = this.activeTab();
    const notificationsToUpdate = this.notifications();
    
    if (currentTab === 'whats-new') {
      // Mark all unread notifications as read
      notificationsToUpdate.forEach(notification => {
        this.notificationService.markAsRead(notification.id);
      });
    } else {
      // For previous tab, we could implement deletion logic
      // For now, just mark them all as read (they're already read)
      console.log('Previous notifications cleared');
    }
  }

  togglePin(notification: Notification) {
    this.notificationService.togglePin(notification.id);
  }

  getInitials(name: string): string {
    if (!name) {
      return '?';
    }
    return name
      .split(' ')
      .filter(Boolean)
      .map(part => part[0])
      .join('')
      .substring(0, 2)
      .toUpperCase();
  }

  getAvatarClasses(index: number, hasAvatar: boolean): Record<string, boolean> {
    const classes: Record<string, boolean> = {
      'user-avatar': true,
      'has-avatar': hasAvatar,
    };

    if (!hasAvatar) {
      const paletteIndex = (index % 6) + 1;
      classes['avatar-placeholder'] = true;
      classes[`avatar-palette-${paletteIndex}`] = true;
    }

    return classes;
  }

  markAsRead(notification: Notification) {
    this.notificationService.markAsRead(notification.id);
  }


  // For testing: Add this method to simulate new notifications
  // testAddNotification() {
  //   this.notificationService.simulateNewNotification();
  // }

  // Helper method to get date label from timestamp
  getDateLabel(timestamp: string): string {
    if (timestamp.includes('Today')) return '';
    if (timestamp.includes('Yesterday')) return '';
    // For other dates, extract the date part before 'at'
    const parts = timestamp.split(' at ');
    return parts.length > 1 ? parts[0] + ' at ' : '';
  }

  // Helper method to get time value from timestamp
  getTimeValue(timestamp: string): string {
    const parts = timestamp.split(' at ');
    return parts.length > 1 ? parts[1] : timestamp;
  }

  // Handle image loading errors
  onImageError(index: number): void {
    this.imageLoadError[index] = true;
  }

  // Handle successful image loading
  onImageLoad(index: number): void {
    this.imageLoadError[index] = false;
  }

  getDisplayName(notification: Notification): string {
    // If objectDetails exists, use the name from it
    if (notification.objectDetails) {
      // For PROJECT type, use projectName
      if (notification.objCode === 'PROJECT' && notification.objectDetails.projectName) {
        return notification.objectDetails.projectName;
      }
      // For TASK type, use taskName
      if (notification.objCode === 'TASK' && notification.objectDetails.taskName) {
        return notification.objectDetails.taskName;
      }
      // For PEOPLE type, use personName
      if (notification.objCode === 'PEOPLE' && notification.objectDetails.personName) {
        return notification.objectDetails.personName;
      }
      // For TIMESHEET type, use timesheetName
      if (notification.objCode === 'TIMESHEET' && notification.objectDetails.timesheetName) {
        return notification.objectDetails.timesheetName;
      }
      // For REVISION type, use revisionName
      if (notification.objCode === 'REVISION' && notification.objectDetails.revisionName) {
        return notification.objectDetails.revisionName;
      }
      // For REQUEST type, use requestName
      if (notification.objCode === 'REQUEST' && notification.objectDetails.requestName) {
        return notification.objectDetails.requestName;
      }
      // For UPDATES type, use updateName
      if (notification.objCode === 'UPDATES' && notification.objectDetails.updateName) {
        return notification.objectDetails.updateName;
      }
      // For ISSUE type, use issueName
      if (notification.objCode === 'ISSUE' && notification.objectDetails.issueName) {
        return notification.objectDetails.issueName;
      }
      // Fallback to name or title from objectDetails
      if (notification.objectDetails.name) {
        return notification.objectDetails.name;
      }
      if (notification.objectDetails.title) {
        return notification.objectDetails.title;
      }
    }
    // Fallback to project field (which may contain name or objCode) or empty string
    return notification.project || '';
  }

   /**
   * Handle notification click - route to the respective page
   */
   onNotificationClick(notification: Notification, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }

    // Get route based on notification object details
    const route = this.notificationService.getNotificationRoute(notification);
    
    if (route) {
      // Get fragment for scrolling to specific update/reply
      const fragment = this.notificationService.getNotificationFragment(notification);
      
      // Navigate with fragment if available
      if (fragment) {
        this.router.navigate(route, { fragment });
      } else {
        this.router.navigate(route);
      }
    }
  }
}
